import { ReactElement, useContext, useEffect, useState } from "react";
import PortfolioService from "../../../../../services/PortfolioService";
import RatioTable from "../../../../../components/RatioTable/RatioTable";
import { Dropdown, IDropdownOption, Spinner, TooltipHost } from "@fluentui/react";
import { TButtonsTable, TRatiosCarterasData } from "../../types/CarteraRatioTypes";
import { dropdownStylesMultiple } from "../../Styles/DropDownStyles";
import { MapRatiosToGraph, TDataSeries } from "../../Util/MapRatiosToGraph";
import HighchartsReactCustom, { TGraphDataChart } from "../../../../../components/HighChartsReactCustom/HighChartsReactCustom";
import React from "react";
import { useBoolean } from "@fluentui/react-hooks";
import { tipImages, tipTexts } from "../../TooltipData/TooltipData";
import CustomTeachingBubble from "../../../../../components/CustomTeachingBubble/CustomTeachingBubble";
import { buttonsGraphList, buttonsTableList } from "../../Util/texts";
import { TActivePlan } from "../../types/TActivePlan";
import { TableData } from "../../Util/TableData";

const BASE_CLASS = "analisis_cuantitativo";

type TAnalisisCuantitativoProps = {
    activePlan: TActivePlan;
    options: IDropdownOption[];
}

const AnalisisCuantitativo = ({ activePlan, options } : TAnalisisCuantitativoProps ) => {

    const [tooltipRatio, { setTrue: showTooltipRatio, setFalse: hideTooltipRatio }] = useBoolean(false);
    const [tablePeriod, setTablePeriod] = useState<string>('Inception');

    // SELECTED
    const [selectedWallets, setSelectedWallets] = useState<IDropdownOption[]>([]);

    // SAVED
    const [walletsSaved, setWalletsSaved] = useState<TRatiosCarterasData[]>([]);
    const [graphRatiosSaved, setGraphRatiosSaved] = useState<TGraphDataChart[]>([]);

    // STATE BUTTONS GRAPHS
    const [graphRatio, setGraphRatio] = useState<TButtonsTable>({text: 'Retorno anualizado', value: 'linear_return'});
    const [secondGraphRatio, setSecondGraphRatio] = useState<TButtonsTable>({text: 'Volatilidad', value: 'volatility'});

    // STATE DATA GRAPHS
    const [graphRatioData, setGraphRatioData] = useState<TGraphDataChart[]>([]);
    const [secondGraphRatioData, setSecondGraphRatioData] = useState<TGraphDataChart[]>([]);
    const [walletsData, setWalletsData] = useState<TRatiosCarterasData[]>([]);

    const [isloadingRatioCartera, setIsLoadingRatioCartera] = useState<boolean>(true);
    const [isloadingGraph, setIsloadingGraph] = useState<boolean>(true);
    const [isloadingSecondGraph, setIsloadingSecondGraph] = useState<boolean>(true);

    
    const getRatiosCarteras = async ({name, contrato}:{name: string, contrato: string}): Promise<TRatiosCarterasData> => {
        const result = await PortfolioService.getRatioCarteraByNomcontrato(contrato)
        const ratiosData: TRatiosCarterasData = {name: name, data: result};
        return ratiosData;   
    };

    const getRatioFromPeriod = async ({ratio, contrato, walletName}: {ratio: string, contrato: string, walletName: string}): Promise<TGraphDataChart> => {
        try {
            const result = await PortfolioService.getRatioCarteraByRatioAndPeriodAndNomcontrato({ratio: ratio, period: tablePeriod, nomcontrato: contrato})
            const dataGraph = MapRatiosToGraph({ratios: result});
            const graphDataWithName: TGraphDataChart = { walletName: walletName, ratio: ratio, contrato: contrato, period: tablePeriod, data: dataGraph };
            return graphDataWithName;
        } catch(error) {
            console.error(error)
            const dataEmpty = [] as TDataSeries[]
            const graphDataWithNameEmpty: TGraphDataChart = {walletName: walletName, ratio: ratio, contrato: contrato, period: tablePeriod, data: dataEmpty };
            return graphDataWithNameEmpty;
            }
    };

    const handleRenderTitle = (items: IDropdownOption[] | undefined): ReactElement<any, any> =>
    {
        if(items)
        {
            if(items.length === 1)
            {
                return <React.Fragment>{`${items[0].text}`}</React.Fragment>;
            }
            return <React.Fragment>{"Cartera resumen"}</React.Fragment>;
        }
        return <React.Fragment></React.Fragment>;
    };

    const onUpdateSelectionWallets = async (event: React.FormEvent<HTMLDivElement>, option: IDropdownOption | undefined): Promise<void> => {
        setIsLoadingRatioCartera(true);
        if (!option) {
            console.warn("Option is undefined");
            return;
        }

        setSelectedWallets(prevState =>
            option.selected
                ? [...prevState, option]
                : prevState.filter(item => item.key !== option.key)
        );
    };

    const updateWallets = async () => {
        const walletsDataFiltered = selectedWallets.map(async (selection: IDropdownOption) => {
            const walletExist = 
                walletsData.find((wallet: TRatiosCarterasData) => wallet.name === selection.text) ||
                walletsSaved.find((ratioWallet: TRatiosCarterasData) => ratioWallet.name === selection.text);

            return walletExist ?? getRatiosCarteras({ name: selection.text, contrato: selection.key.toString() })
                .then(result => {
                    setWalletsSaved(prevState =>
                        prevState.some(item => item.name === result.name) ? prevState : [...prevState, result]
                    );
        
                    return result;
                })
                .catch (err => {
                    console.error(err);
                    return null;
                }) 
        });

        setWalletsData((await Promise.all(walletsDataFiltered))
            .filter((item): item is TRatiosCarterasData => { 
                setIsLoadingRatioCartera(false);
                return item !== null;
            }));
    };

    const updateGraphs = async ({ graphRatioSelected, setState, setLoader } : { 
        graphRatioSelected: TButtonsTable, 
        setState: Function,
        setLoader: Function
    }) => {
        setLoader(true);
        const graphsData = selectedWallets.map(async (selection: IDropdownOption) => {
            const foundGraphSaved = graphRatiosSaved?.find(graphData =>
                    graphData.walletName === selection.text &&
                    graphData.ratio === graphRatioSelected.value &&
                    graphData.period === tablePeriod);

            return foundGraphSaved ?? getRatioFromPeriod({ 
                    ratio: graphRatioSelected.value, 
                    walletName: selection.text, 
                    contrato: selection.key.toString() 
                }).then(resultFromService => {
                    setGraphRatiosSaved(prevState => [...prevState, resultFromService]);
                    return resultFromService;
                }).catch(err => {
                    console.error("Error obteniendo ratio:", err);
                    return null;
                })
        });
        setState([]);
        setState((await Promise.all(graphsData)).filter((item): item is TGraphDataChart => {
            setLoader(false);
            return item !== null;
        }));
    };

    useEffect(() => {
            updateGraphs({ graphRatioSelected: graphRatio, 
                setState: setGraphRatioData, setLoader: setIsloadingGraph });
            updateGraphs({ graphRatioSelected: secondGraphRatio, 
                setState: setSecondGraphRatioData, setLoader: setIsloadingSecondGraph });
    }, [selectedWallets, tablePeriod, graphRatio, secondGraphRatio])

    useEffect(() => {
        updateWallets();
    }, [selectedWallets])

    useEffect(() => {
        getRatiosCarteras({name: activePlan.name, contrato: activePlan.finametrixContract})
            .then((result: TRatiosCarterasData) => {
                setWalletsData([result]);
                setWalletsSaved([result]);
                setIsLoadingRatioCartera(false);
                setSelectedWallets([{
                    key: result.data[0].nomcontrato,
                    text: result.name,
                    id: activePlan.id.toString()
                }]);
            }
        );
    }, []);



    const resultTable = TableData({ tablePeriod: tablePeriod, ratios: walletsData })

    return (
            <div className={`${BASE_CLASS}__body__container`}>

                <div className={`${BASE_CLASS}__left__container`}>
                    <div className={`${BASE_CLASS}__dropdown__container`}>
                        <Dropdown placeholder={activePlan.name}
                            className={`${BASE_CLASS}__dropdown`}
                            options={options as IDropdownOption[]}
                            defaultSelectedKeys={[activePlan.finametrixContract]}
                            onRenderTitle={handleRenderTitle}
                            onChange={onUpdateSelectionWallets}
                            styles={dropdownStylesMultiple}
                            multiSelect
                        />
                    </div>
                    
                    <RatioTable titleCard="Ratios Cartera" ratios={walletsData} resultTable={resultTable} isLoading={isloadingRatioCartera} /> 
                    
                </div>
                <div className={`${BASE_CLASS}__right__container`}>
                    <div id="table_perdiod_id" className={`${BASE_CLASS}__buttons__period`}>
                        {buttonsTableList.map((buttonTable: TButtonsTable) => (
                            <button className={`${tablePeriod === buttonTable.value ? 'active' : ''} fileButtons`} 
                                onClick={() => {
                                    setTablePeriod(buttonTable.value)
                                }}>{buttonTable.text}</button>
                        ))}

                        <img id="tip_location" onMouseEnter={showTooltipRatio} onClick={showTooltipRatio} style={{ verticalAlign: 'bottom' }} src="/icons/informationBlack.svg" alt="Información" />

                        {tooltipRatio && <CustomTeachingBubble texts={tipTexts} images={tipImages} targetIdentifier="tip_location" showTooltip={hideTooltipRatio} />}
                    </div>
                    <div className={`${BASE_CLASS}__graph__container`}>
                        {isloadingGraph && <Spinner size={3} style={{ padding: "1em" }} />}
                        <div className={`${BASE_CLASS}__graph__card`}>
                            <div className={`${BASE_CLASS}__buttons__graph`}>
                                {buttonsGraphList.map((buttonTable: TButtonsTable) => (
                                    <TooltipHost content={buttonTable.toolTipHostContent}>
                                        <button className={`${graphRatio.value === buttonTable.value ? 'active' : ''}`} 
                                            onClick={() => {
                                                setGraphRatio({
                                                    text: buttonTable.text,
                                                    value: buttonTable.value
                                                })
                                            }}>
                                                {buttonTable.text}
                                        </button>
                                    </TooltipHost>
                                ))}
                            </div>
                            <HighchartsReactCustom name={graphRatio.text} graphDataList={graphRatioData} />
                        </div>
                        {isloadingSecondGraph && <Spinner size={3} style={{ padding: "1em" }} />}
                        <div className={`${BASE_CLASS}__graph__card`}>
                            <div className={`${BASE_CLASS}__buttons__graph`}>
                                {buttonsGraphList.map((buttonTable: TButtonsTable) => (
                                    <TooltipHost content={buttonTable.toolTipHostContent}>
                                        <button className={`${secondGraphRatio.value === buttonTable.value ? 'active' : ''}`} 
                                            onClick={() => {
                                                setSecondGraphRatio({
                                                    text: buttonTable.text,
                                                    value: buttonTable.value
                                                })
                                            }}>
                                                {buttonTable.text}
                                        </button>
                                    </TooltipHost>
                                ))}
                            </div>
                            <HighchartsReactCustom name={secondGraphRatio.text} graphDataList={secondGraphRatioData} />
                        </div>
                    </div>
                </div>
            </div>

    )
}

export default AnalisisCuantitativo;